import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Security, Save, Cancel } from '@mui/icons-material';

import RoleBasicInfoSection from './RoleBasicInfoSection';
import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';

const RoleFormDialog = ({ open, onClose, role, onSave, loading = false }) => {
  const basicInfoRef = useRef(null);

  const [basicInfo, setBasicInfo] = useState({});
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});

  const isEditMode = Boolean(role);

  useEffect(() => {
    if (role) {
      setFacilityPermissions(role.facility_permissions || {});
      setOrganizationPermissions(role.organization_permissions || {});
    } else {
      setFacilityPermissions({});
      setOrganizationPermissions({});
    }
  }, [role, open]);

  const handleBasicInfoChange = (newBasicInfo) => {
    setBasicInfo(newBasicInfo);
  };

  const handleFacilityPermissionChange = (newPermissions) => {
    setFacilityPermissions(newPermissions);
  };

  const handleOrganizationPermissionChange = (newPermissions) => {
    setOrganizationPermissions(newPermissions);
  };

  const handleApplyTemplate = (template) => {
    if (template.facilityPermissions) {
      setFacilityPermissions(template.facilityPermissions);
    }
    if (template.organizationPermissions) {
      setOrganizationPermissions(template.organizationPermissions);
    }
    if (template.permissions && !template.facilityPermissions) {
      setFacilityPermissions(template.permissions);
    }
  };

  const handleSave = () => {
    if (basicInfoRef.current && basicInfoRef.current.validateForm()) {
      const formData = {
        name: basicInfo.name,
        description: basicInfo.description,
        organization_id: basicInfo.organizationId,
        is_default: basicInfo.isDefault,
        has_all_facilities_access: basicInfo.hasAllFacilitiesAccess,
        facility_permissions: facilityPermissions,
        organization_permissions: organizationPermissions,
      };
      onSave(formData);
    }
  };

  const isFormDisabled = !basicInfoRef.current?.isFormValid || loading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color='primary' />
          {isEditMode ? 'Edit Role' : 'Create New Role'}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <RoleBasicInfoSection
            ref={basicInfoRef}
            role={role}
            onFormChange={handleBasicInfoChange}
          />

          <Divider sx={{ my: 3 }} />

          <PermissionsSection
            permissions={organizationPermissions}
            onPermissionChange={handleOrganizationPermissionChange}
            onApplyTemplate={handleApplyTemplate}
            permissionLevel='organization'
          />

          <Divider sx={{ my: 3 }} />

          <PermissionsSection
            permissions={facilityPermissions}
            onPermissionChange={handleFacilityPermissionChange}
            onApplyTemplate={handleApplyTemplate}
            permissionLevel='facility'
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 3 }}>
        <Button onClick={onClose} startIcon={<Cancel />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          startIcon={
            loading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              <Save sx={{ color: 'white' }} />
            )
          }
          disabled={isFormDisabled}
          sx={{
            color: 'white',
            '& .MuiButton-startIcon': {
              color: 'white',
            },
          }}>
          {loading ? 'Saving...' : isEditMode ? 'Update Role' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormDialog;
